import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Copy, ExternalLink, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@artstorefronts/ui';
import { PREVIEW_PARAM } from '@arthelper/shared/acquisition/strategy';
import { townSquareUrl } from '../../welcome/useWaiting';
import { PREVIEW_STRATEGY } from './queries';
import { errorMessage } from './shared';

/**
 * Try a strategy as a brand-new visitor would — drafts included, before anyone sees it. Each
 * link is a fresh visitor pinned to this strategy: the home page gate, signup, the waitlist and
 * the waiting wizard all behave as this strategy says, and nothing done through it counts in
 * the metrics.
 */
export function PreviewStrategy({ strategyId }: { strategyId: string }) {
  const [link, setLink] = useState<string | null>(null);
  const [preview, { loading }] = useMutation<{ previewAcquisitionStrategy: string }>(PREVIEW_STRATEGY);

  const handlePreview = async () => {
    try {
      const { data } = await preview({ variables: { id: strategyId } });
      if (!data) return;
      setLink(townSquareUrl(`/?${PREVIEW_PARAM}=${data.previewAcquisitionStrategy}`));
    } catch (err) {
      toast.error(errorMessage(err, 'Could not make a preview link.'));
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success('Preview link copied');
  };

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4" data-testid="strategy-preview">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-gray-900">Try it yourself</h2>
          <p className="text-sm text-gray-600">
            Walk this strategy as a new visitor: the home page, signup, the waitlist and the waiting wizard.
          </p>
        </div>
        <Button type="button" className="h-11 gap-1.5 md:h-9" disabled={loading} onClick={() => void handlePreview()}>
          <Eye className="h-3.5 w-3.5" aria-hidden />
          {loading ? 'Making a link…' : link ? 'New preview link' : 'Get a preview link'}
        </Button>
      </div>
      {link && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 min-w-0 items-center gap-1.5 break-all text-sm font-medium text-blue-700 underline underline-offset-4"
            >
              <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Open the preview
            </a>
            <Button type="button" variant="outline" className="h-11 gap-1.5 md:h-9" onClick={() => void handleCopy()}>
              <Copy className="h-3.5 w-3.5" aria-hidden />
              Copy link
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Open it in a private window: signed in, you skip the gate. Signing up through it makes a real test account,
            but nothing done here counts in this strategy&apos;s metrics.
          </p>
        </div>
      )}
    </section>
  );
}
